import React, { useEffect, useState } from 'react'
import {
  Alert,
  Box, Card, CardContent, Typography, Button, Stack, Tab, Tabs, Chip, Avatar, Grid, Paper, Fab, Skeleton,
  Accordion, AccordionSummary, AccordionDetails, Link as MuiLink,
} from '@mui/material'
import {
  Business as BusinessIcon,
  Label as LabelIcon,
  Verified as VerifiedIcon,
  ArrowBack as ArrowBackIcon,
  Calculate as CalculateIcon,
  CheckCircle as CheckCircleIcon,
  FilePresent as FilePresentIcon,
  ExpandMore as ExpandMoreIcon,
  OpenInNew as OpenInNewIcon,
  PlayCircleOutline as PlayCircleIcon,
  EmojiObjects as EmojiObjectsIcon,
  PersonSearch as PersonSearchIcon,
} from '@mui/icons-material'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { produtoService, parceiroService } from '@/services/produtoService'
import { SIMULADOR_HABILITADO } from '@/config/features'
import { ehConsultor } from '@/components/layout/menu'
import { Produto, Parceiro } from '@/types/produto'
import { authStore } from '@/store/authStore'
import EditIcon from '@mui/icons-material/Edit'
import {
  corCategoria,
  iconeCategoria,
  rotuloCategoria,
} from '@/constants/categoriasProduto'
import PerguntasElegibilidade from '@/components/produtos/PerguntasElegibilidade'
import PoliticaElegibilidadeProduto from '@/components/produtos/PoliticaElegibilidadeProduto'
import ChecklistVersionadoEditor from '@/components/produtos/ChecklistVersionadoEditor'
import { SecaoProduto } from '@/components/produtos/SecaoProduto'
import CamposDiagnostico from '@/components/produtos/CamposDiagnostico'
import FormularioProdutoEditor from '@/components/produtos/FormularioProdutoEditor'
import PoliticaParceiroDialog from '@/components/produtos/PoliticaParceiroDialog'
import AbaInformativo from '@/components/produtos/AbaInformativo'
import PoliticaVigentePainel from '@/components/produtos/PoliticaVigentePainel'
import SwitchAtivoProduto from '@/components/produtos/SwitchAtivoProduto'
import { AlterarStatusProduto, ChipStatusProduto } from '@/components/produtos/StatusProdutoCatalogo'
import { CODIGO_OUTRA_NECESSIDADE, rotuloStatusProduto } from '@/constants/codigosProduto'

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

/**
 * Painel de aba do produto (09/09): o mesmo recuo em todos os lados, alinhado ao px das Tabs.
 * Antes só havia padding no topo e o conteúdo de cada aba encostava na borda do cartão —
 * cada aba parecia ter um layout próprio. Toda aba renderiza seções `SecaoProduto`.
 */
function TabPanel({ children, value, index }: TabPanelProps) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ p: { xs: 2, md: 3 } }}>{children}</Box>}
    </div>
  )
}

type AbaProduto =
  | 'caracteristicas' | 'elegibilidade' | 'checklist' | 'diagnostico' | 'formularios' | 'parceiros' | 'biblioteca'
  | 'informativo' | 'politica'

/**
 * Formulários (AN-6 §6, L5) vem depois de Diagnóstico: são os dados que a reserva precisa ter,
 * por etapa (Cadastro/Estruturação), versionados. Aba só do back-office — fora de
 * ABAS_CONSULTOR; o ADMIN edita e publica, os demais perfis leem.
 */
const ABAS_BACKOFFICE: AbaProduto[] = [
  'caracteristicas', 'elegibilidade', 'checklist', 'diagnostico', 'formularios', 'parceiros', 'biblioteca', 'informativo',
  'politica',
]
/**
 * VC-11: o consultor (chega por URL direta — a lista abre o informativo) vê só o cabeçalho,
 * Características e o Informativo (PDF) vigente. Elegibilidade, Diagnóstico, Parceiros e
 * Biblioteca são cadastro do back-office.
 *
 * Onda 5 (AN-11 §1.4): a aba "Manual PDF" virou "Informativo (PDF)" — o vigente publicado pelo
 * ADMIN (documentos), não mais "o primeiro MANUAL" da listagem.
 */
/**
 * P6 (AN-PP6 §2.4/§8): "Política vigente" é a única aba de política que o consultor vê — o retrato agregado,
 * global (mesmo para qualquer tenant), sem pendentes e sem nomes de quem propôs/aprovou.
 */
const ABAS_CONSULTOR: AbaProduto[] = ['caracteristicas', 'informativo', 'politica']
const ABAS_COM_LINK: AbaProduto[] = ['elegibilidade', 'checklist', 'formularios', 'informativo', 'politica']

const ProdutoDetailPage: React.FC = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const usuario = authStore((s) => s.usuario)
  const isAdmin = !!usuario?.permissoes?.some((p) => p.toUpperCase() === 'ADMIN')
  // V64 (AN-PP6 §3.2): formulário e checklist são aprovados por GERENTE ou ADMIN que não seja o proponente.
  const podeAprovarPolitica = !!usuario?.permissoes?.some((p) => ['ADMIN', 'GERENTE'].includes(p.toUpperCase()))
  // Quem cadastra a política do parceiro é o back-office, não só o ADMIN: o backend exige
  // OPERADOR+, e é o operador que conversa com o parceiro.
  const podeConfigurarParceiro = !!usuario?.permissoes?.some((p) =>
    ['ADMIN', 'GERENTE', 'OPERADOR'].includes(p.toUpperCase())
  )
  const [politicaAberta, setPoliticaAberta] = useState<{ id: string; nome: string } | null>(null)
  const consultor = ehConsultor(usuario?.permissoes)
  const abas = consultor ? ABAS_CONSULTOR : ABAS_BACKOFFICE
  const [searchParams] = useSearchParams()
  // `?aba=elegibilidade` abre direto na aba do checklist (link do formulário do produto, 08/09).
  // Para o consultor a aba não existe e o link cai em Características.
  const [tabValue, setTabValue] = useState(() => {
    const pedida = searchParams.get('aba')
    return ABAS_COM_LINK.includes(pedida as AbaProduto) ? Math.max(abas.indexOf(pedida as AbaProduto), 0) : 0
  })
  const indiceDa = (aba: AbaProduto) => abas.indexOf(aba)
  const [produto, setProduto] = useState<Produto | null>(null)
  const [parceiros, setParceiros] = useState<Parceiro[]>([])
  const [loading, setLoading] = useState(true)
  const [erro, setErro] = useState<string | null>(null)

  useEffect(() => {
    if (!id) return
    let ativo = true
    setLoading(true)
    Promise.all([
      produtoService.buscar(id),
      parceiroService.listar().catch(() => [] as Parceiro[]),
    ])
      .then(([p, todosParceiros]) => {
        if (!ativo) return
        setProduto(p)
        // O produto traz apenas os IDs dos parceiros; resolvemos contra a lista completa.
        const ids = new Set(p.parceiros ?? [])
        setParceiros((todosParceiros as Parceiro[]).filter((pc) => ids.has(pc.id)))
      })
      .catch((e) => { if (ativo) setErro(e?.message ?? 'Produto não encontrado') })
      .finally(() => { if (ativo) setLoading(false) })
    return () => { ativo = false }
  }, [id])

  if (loading) {
    return (
      <Box>
        <Skeleton variant="rectangular" height={220} sx={{ borderRadius: 3, mb: 3 }} />
        <Grid container spacing={3}>
          {Array.from({ length: 4 }).map((_, i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
      </Box>
    )
  }

  if (erro || !produto) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {erro ?? 'Produto não encontrado'}
        </Alert>
        <Button variant="contained" onClick={() => navigate('/produtos')}>Voltar ao Catálogo</Button>
      </Box>
    )
  }

  const cor = corCategoria(produto.categoria)
  const icon = iconeCategoria(produto.categoria)
  const categoriaLabel = rotuloCategoria(produto.categoria)
  // P1 (AN-PP1 §2): RETIRADA é terminal — sem edição nem reativação pela tela (o servidor também recusa).
  const retirado = produto.status === 'RETIRADA'
  const outraNecessidade = produto.codigo === CODIGO_OUTRA_NECESSIDADE
  const numeroParceiros = produto.parceiros?.length ?? 0
  const criterios = produto.criteriosElegibilidade ?? []
  const documentos = produto.documentacaoNecessaria ?? []
  const beneficios = produto.beneficios ?? []
  const materiais = produto.materiaisComerciais ?? []
  const faq = produto.faq ?? []
  // A aba mostra um convite a preencher em vez de cinco seções vazias empilhadas.
  const temBiblioteca =
    !!produto.perfilCliente || !!produto.videoUrl ||
    beneficios.length > 0 || materiais.length > 0 || faq.length > 0

  // P6: destino do "Ver detalhe" de cada componente da política vigente (tela dona; nada é editado na aba).
  const ABA_DO_COMPONENTE: Record<string, AbaProduto> = {
    elegibilidade: 'elegibilidade', checklist: 'checklist', formularios: 'formularios',
  }
  const detalhePolitica = [
    ...Object.keys(ABA_DO_COMPONENTE),
    ...(isAdmin ? ['simulacao', 'comissao', 'tributos'] : []),
  ]
  const abrirDetalhePolitica = (componente: string) => {
    const aba = ABA_DO_COMPONENTE[componente]
    if (aba && indiceDa(aba) >= 0) setTabValue(indiceDa(aba))
    else if (isAdmin) navigate('/simulacoes/politicas')
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/produtos')}>Produtos</Button>
        <Typography color="text.secondary">/</Typography>
        <Typography fontWeight={600}>{produto.nome}</Typography>
        <Box sx={{ flex: 1 }} />
        {/* Onda 5 (AN-11 §2.3): ADMIN alterna; GERENTE/OPERADOR veem o selo; consultor, nada. */}
        {/* Fix P1: OTHER_NEED não pode ser inativado (422 STATUS_FIXO_DO_PRODUTO) — o interruptor some. */}
        {!consultor && !outraNecessidade && (
          <SwitchAtivoProduto produto={produto} podeAlterar={isAdmin && !retirado} onAlterado={setProduto} />
        )}
        {/* P1 (AN-PP1 §2.2): status normativo — só ADMIN altera (PATCH /status). */}
        {isAdmin && <AlterarStatusProduto produto={produto} onAlterado={setProduto} />}
        {isAdmin && !retirado && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/produtos/${produto.id}/editar`)}
          >
            Editar
          </Button>
        )}
      </Box>

      <Box sx={{
        background: `linear-gradient(135deg, ${cor} 0%, ${cor}cc 100%)`,
        borderRadius: 3,
        p: 4,
        color: 'white',
        mb: 4,
        position: 'relative',
      }}>
        <Stack direction="row" alignItems="flex-start" spacing={3}>
          <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.3)', color: 'white', width: 80, height: 80, fontSize: '2rem' }}>
            {icon}
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" fontWeight={700} mb={1}>{produto.nome}</Typography>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
              <Chip label={categoriaLabel} sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 600 }} />
              <ChipStatusProduto status={produto.status} size="medium" sx={{ fontWeight: 600 }} />
            </Stack>
            <Typography variant="body1" sx={{ mt: 2, lineHeight: 1.7 }}>
              {produto.descricao}
            </Typography>
          </Box>
        </Stack>
      </Box>

      {/*
        P1 (AN-PP1 §6): taxa, valor e prazo eram demonstrativos e saíram da tela — o PDF de
        25/10 proíbe exibi-los como política. Ficam código (estável, §1), status e parceiros.
      */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={4}>
          <Card sx={{ textAlign: 'center', borderRadius: 2, height: '100%' }}>
            <CardContent>
              <LabelIcon sx={{ fontSize: 32, color: cor, mb: 1 }} />
              <Typography variant="caption" color="text.secondary" display="block">Código</Typography>
              <Typography variant="body2" fontWeight={700} sx={{ fontFamily: 'monospace' }}>
                {produto.codigo ?? '—'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ textAlign: 'center', borderRadius: 2, height: '100%' }}>
            <CardContent>
              <VerifiedIcon sx={{ fontSize: 32, color: cor, mb: 1 }} />
              <Typography variant="caption" color="text.secondary" display="block">Status</Typography>
              <Typography variant="body2" fontWeight={700}>{rotuloStatusProduto(produto.status)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ textAlign: 'center', borderRadius: 2, height: '100%' }}>
            <CardContent>
              <BusinessIcon sx={{ fontSize: 32, color: cor, mb: 1 }} />
              <Typography variant="caption" color="text.secondary" display="block">Parceiros</Typography>
              <Typography variant="body2" fontWeight={700}>{numeroParceiros} disponíveis</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card sx={{ borderRadius: 2 }}>
        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ borderBottom: '1px solid', borderColor: 'divider', px: 3 }}>
          {/*
            Diagnóstico vem logo depois de Elegibilidade porque é a ordem da jornada:
            primeiro se decide se o produto serve (elegibilidade + checklist), depois o
            que perguntar ao cliente na estruturação. A ordem vem de ABAS_*.
          */}
          {abas.map((aba) => (
            <Tab
              key={aba}
              label={
                {
                  caracteristicas: 'Características',
                  elegibilidade: 'Elegibilidade',
                  checklist: 'Checklist',
                  diagnostico: 'Diagnóstico',
                  formularios: 'Formulários',
                  parceiros: 'Parceiros',
                  biblioteca: 'Biblioteca',
                  informativo: 'Informativo (PDF)',
                  politica: 'Política vigente',
                }[aba]
              }
            />
          ))}
        </Tabs>

        <TabPanel value={tabValue} index={indiceDa('caracteristicas')}>
          <SecaoProduto
            titulo="Dados do catálogo"
            descricao="Identificação estável do produto. O código não muda e é o que os outros módulos usam — o nome é só exibição."
            acao={
              isAdmin && !retirado ? (
                <Button variant="outlined" startIcon={<EditIcon />} onClick={() => navigate(`/produtos/${id}/editar`)}>
                  Editar produto
                </Button>
              ) : undefined
            }
          >
            <Grid container spacing={2}>
              {[
                { rotulo: 'Código', valor: produto.codigo ?? '—', mono: true },
                { rotulo: 'Categoria', valor: categoriaLabel, mono: false },
                { rotulo: 'Status', valor: rotuloStatusProduto(produto.status), mono: false },
              ].map((r) => (
                <Grid item xs={12} sm={4} key={r.rotulo}>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                    <Typography variant="caption" color="text.secondary" component="div">
                      {r.rotulo}
                    </Typography>
                    <Typography
                      variant="subtitle1"
                      component="div"
                      sx={{ fontWeight: 700, ...(r.mono && { fontFamily: 'monospace' }) }}
                    >
                      {r.valor}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
            {retirado && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Produto retirado do catálogo: não aceita novas reservas e não aparece para os consultores.
                As reservas existentes continuam como histórico.
              </Alert>
            )}
            {produto.descricaoDetalhada && (
              <Typography variant="body2" sx={{ mt: 2, whiteSpace: 'pre-line' }}>
                {produto.descricaoDetalhada}
              </Typography>
            )}
          </SecaoProduto>
        </TabPanel>

        <TabPanel value={tabValue} index={indiceDa('elegibilidade')}>
          {/*
            Ordem da aba: primeiro a POLÍTICA de elegibilidade versionada (P2, AN-PP2 §2 — fonte
            única; substitui as colunas de faturamento/LTV da V9), depois as perguntas adicionais e,
            por último, a prosa da biblioteca. O checklist documental mudou para a aba Checklist (P3).
          */}
          <Stack spacing={3}>
            {id && (
              <PoliticaElegibilidadeProduto
                produtoId={id}
                produtoCodigo={produto.codigo}
                podeEditar={isAdmin}
                retirado={retirado}
                podeAprovar={!consultor && !!usuario?.permissoes?.some((p) => ['ADMIN', 'GERENTE'].includes(p.toUpperCase()))}
                usuarioId={usuario?.id ?? null}
              />
            )}

            {criterios.length > 0 && (
              <SecaoProduto
                titulo="Critérios descritivos"
                descricao="Texto da biblioteca do produto — não é avaliado pelo motor."
              >
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {criterios.map((c, i) => (
                    <Chip key={i} label={c} variant="outlined" icon={<CheckCircleIcon />} />
                  ))}
                </Stack>
              </SecaoProduto>
            )}

            {id && <PerguntasElegibilidade produtoId={id} podeEditar={isAdmin} />}

            <SecaoProduto
              titulo="Documentação necessária (descrição)"
              descricao="Texto informativo da biblioteca do produto. Não é o que a reserva cobra — isso é a aba Checklist."
            >
              {documentos.length === 0 ? (
                <Typography variant="body2" color="text.secondary">Nenhum documento listado.</Typography>
              ) : (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {documentos.map((d, i) => (
                    <Chip key={i} label={d} variant="outlined" icon={<FilePresentIcon />} />
                  ))}
                </Stack>
              )}
            </SecaoProduto>
          </Stack>
        </TabPanel>

        {/*
          O formulário do Diagnóstico é montado a partir destes campos: o que se cadastra
          aqui é exatamente o que o consultor responde na etapa 1 da estruturação.
        */}
        {/*
          P3 (AN-PP3 §9): checklist documental VERSIONADO — vigente, rascunho, diferença, histórico.
          Aba do back-office (fora de ABAS_CONSULTOR): leitura para OPERADOR/GERENTE, edição só ADMIN.
        */}
        <TabPanel value={tabValue} index={indiceDa('checklist')}>
          {id && (
            <ChecklistVersionadoEditor
              produtoId={id}
              podeEditar={isAdmin}
              podeAprovar={podeAprovarPolitica}
              usuarioId={usuario?.id ?? null}
            />
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={indiceDa('diagnostico')}>
          {id && <CamposDiagnostico produtoId={id} podeEditar={isAdmin} />}
        </TabPanel>

        <TabPanel value={tabValue} index={indiceDa('formularios')}>
          {id && (
            <FormularioProdutoEditor
              produtoId={id}
              podeEditar={isAdmin}
              podeAprovar={podeAprovarPolitica}
              usuarioId={usuario?.id ?? null}
            />
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={indiceDa('parceiros')}>
          <SecaoProduto
            titulo="Instituições Parceiras Credenciadas"
            descricao="Parceiros que operam este produto. A política de elegibilidade é do par parceiro × produto e abre no cartão de cada um."
          >
          {parceiros.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Nenhum parceiro vinculado a este produto.
            </Typography>
          ) : (
            <Grid container spacing={3}>
              {parceiros.map((parceiro) => (
                <Grid item xs={12} sm={6} md={4} key={parceiro.id}>
                  <Card sx={{ borderRadius: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <CardContent>
                      <Box sx={{ textAlign: 'center', mb: 2 }}>
                        <Avatar sx={{ width: 56, height: 56, mx: 'auto', bgcolor: 'primary.main' }}>
                          {parceiro.nome?.[0] ?? '?'}
                        </Avatar>
                      </Box>
                      <Typography variant="subtitle2" fontWeight={700} textAlign="center">{parceiro.nome}</Typography>
                      <Box sx={{ textAlign: 'center', mb: 2 }}>
                        <Chip size="small" label={parceiro.tipo} sx={{ mt: 0.5 }} />
                      </Box>
                      <Stack spacing={1.5}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                          <Typography variant="caption" color="text.secondary">CNPJ</Typography>
                          <Typography variant="body2" fontWeight={600}>{parceiro.cnpj}</Typography>
                        </Box>
                        {parceiro.email && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                            <Typography variant="caption" color="text.secondary">E-mail</Typography>
                            <Typography variant="body2" fontWeight={600} sx={{ wordBreak: 'break-all', textAlign: 'right' }}>{parceiro.email}</Typography>
                          </Box>
                        )}
                        {parceiro.telefone && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                            <Typography variant="caption" color="text.secondary">Telefone</Typography>
                            <Typography variant="body2" fontWeight={600}>{parceiro.telefone}</Typography>
                          </Box>
                        )}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                          <Typography variant="caption" color="text.secondary">Status</Typography>
                          <Chip
                            size="small"
                            label={parceiro.ativo ? 'Ativo' : 'Inativo'}
                            color={parceiro.ativo ? 'success' : 'default'}
                          />
                        </Box>
                      </Stack>

                      {/*
                        A política é do PAR (parceiro, produto) — "repete para cada produto
                        que o parceiro opera" (PDF). Abrir daqui já determina o par, então
                        não há como preencher a política do produto errado.
                      */}
                      <Button
                        fullWidth
                        variant="outlined"
                        size="small"
                        sx={{ mt: 2 }}
                        onClick={() => setPoliticaAberta({ id: parceiro.id, nome: parceiro.nome })}
                      >
                        Política de elegibilidade
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
          </SecaoProduto>

          {politicaAberta && id && (
            <PoliticaParceiroDialog
              aberto
              parceiroId={politicaAberta.id}
              parceiroNome={politicaAberta.nome}
              produtoId={id}
              podeEditar={podeConfigurarParceiro}
              onFechar={() => setPoliticaAberta(null)}
            />
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={indiceDa('biblioteca')}>
          {!temBiblioteca ? (
            <Alert
              severity="info"
              action={
                isAdmin ? (
                  <Button color="inherit" size="small" onClick={() => navigate(`/produtos/${produto.id}/editar`)}>
                    Preencher
                  </Button>
                ) : undefined
              }
            >
              Este produto ainda não tem material de biblioteca (perfil de cliente, benefícios,
              material comercial, vídeo ou perguntas frequentes).
            </Alert>
          ) : (
            <Stack spacing={3}>
              {produto.perfilCliente && (
                <SecaoProduto titulo="Perfil de Cliente" descricao="Para quem o produto foi desenhado — o consultor usa isso para reconhecer a oportunidade.">
                  <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
                    <Stack direction="row" spacing={1.5} alignItems="flex-start">
                      <PersonSearchIcon sx={{ color: 'primary.main', mt: 0.25, flexShrink: 0 }} />
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                        {produto.perfilCliente}
                      </Typography>
                    </Stack>
                  </Paper>
                </SecaoProduto>
              )}

              {beneficios.length > 0 && (
                <SecaoProduto titulo="Benefícios" descricao="Argumentos de venda do produto, na ordem cadastrada.">
                  <Grid container spacing={2}>
                    {beneficios.map((b, i) => (
                      <Grid item xs={12} sm={6} key={i}>
                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                          <Stack direction="row" spacing={1.5} alignItems="flex-start">
                            <EmojiObjectsIcon sx={{ color: 'warning.main', mt: 0.25, flexShrink: 0 }} />
                            <Typography variant="body2">{b}</Typography>
                          </Stack>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </SecaoProduto>
              )}

              {produto.videoUrl && (
                <SecaoProduto titulo="Vídeo Explicativo" descricao="Abre em nova aba, fora da plataforma.">
                  <Button
                    variant="outlined"
                    startIcon={<PlayCircleIcon />}
                    endIcon={<OpenInNewIcon fontSize="small" />}
                    href={produto.videoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Assistir
                  </Button>
                </SecaoProduto>
              )}

              {materiais.length > 0 && (
                <SecaoProduto titulo="Material Comercial" descricao="Peças para enviar ao cliente. Ficam fora da plataforma (Drive, site) e abrem em nova aba.">
                  <Stack spacing={2}>
                    {materiais.map((m, i) => (
                      <Paper key={i} variant="outlined" sx={{ p: 2.5, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                        <FilePresentIcon sx={{ fontSize: 28, color: 'primary.main' }} />
                        <Box sx={{ flex: 1 }}>
                          {/* Abre em nova aba: as peças ficam fora da plataforma (Drive, site). */}
                          <MuiLink
                            href={m.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="body2"
                            fontWeight={600}
                          >
                            {m.titulo}
                          </MuiLink>
                          {m.tipo && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {m.tipo}
                            </Typography>
                          )}
                        </Box>
                        <OpenInNewIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                      </Paper>
                    ))}
                  </Stack>
                </SecaoProduto>
              )}

              {faq.length > 0 && (
                <SecaoProduto titulo="Perguntas Frequentes" descricao="Dúvidas que costumam aparecer na conversa com o cliente.">
                  {faq.map((f, i) => (
                    <Accordion key={i} disableGutters variant="outlined">
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="body2" fontWeight={600}>{f.pergunta}</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                          {f.resposta}
                        </Typography>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </SecaoProduto>
              )}
            </Stack>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={indiceDa('informativo')}>
          {id && <AbaInformativo produtoId={id} consultor={consultor} podePublicar={isAdmin} />}
        </TabPanel>

        {/*
          P6 (AN-PP6 §8): retrato agregado da política na data, pelo CÓDIGO canônico. "Ver detalhe" leva à aba dona
          (elegibilidade/checklist/formulários) ou às Políticas de simulação (ADMIN) — só para o back-office.
        */}
        <TabPanel value={tabValue} index={indiceDa('politica')}>
          <PoliticaVigentePainel
            codigo={produto.codigo}
            consultor={consultor}
            componentesComDetalhe={detalhePolitica}
            onVerDetalhe={abrirDetalhePolitica}
          />
        </TabPanel>
      </Card>

      {SIMULADOR_HABILITADO && (
        <Fab
          variant="extended"
          color="primary"
          onClick={() => navigate(`/simulacao?produtoId=${produto.id}`)}
          sx={{
            position: 'fixed',
            bottom: { xs: 24, sm: 32 },
            right: { xs: 16, sm: 24 },
            zIndex: 50,
          }}
        >
          <CalculateIcon sx={{ mr: 1 }} />
          Simular Operação
        </Fab>
      )}
    </Box>
  )
}

export default ProdutoDetailPage
